// ONE CONNECTION PER TAB, not one per widget.
//
// `/v1/streams` is a MULTIPLEXER — one connection, several topics — and it was
// being used un-multiplexed: the four call sites each opened their own socket
// with a disjoint single-topic set. `NotificationBell` lives in `AppTopbar`, so
// every page held at least one; Home added `sessions,approvals` and Fleet added
// `tasks`. So a Fleet tab held 2 sockets.
//
// The DB reads are NOT per connection. `apps/api/src/routes/streams.ts` hands
// every topic to a shared poller (`createStreamHub`, `stream-hub.ts`, plan 101
// E21): one read loop per `channelKey` — (topic, account), and (topic, account,
// user) for the per-user `notifications` topic — however many tabs watch it.
// `notifications` also carries a `probe` (`notificationInboxChangeToken`), so an
// idle inbox costs an index-only change-token check per poll and the full
// `listNotificationsForUser` read runs only when that token moves. What a
// connection still costs is its own: a socket, a per-subscriber `diffTopic`, the
// periodic credential recheck, and a slot under `MAX_STREAMS_PER_CREDENTIAL`
// (per credential, per pod), past which the api refuses the dial. Hence still
// one connection per tab.
//
// A component registers the topics it reads and gets the same store back. The
// union is what the socket asks for, so every page holds exactly 1 connection.
//
// The rows stay separated by construction: the store is keyed by `(topic, key)`
// and `list(topic)` reads one topic's slice, so sharing a socket never lets one
// consumer see another's rows.

import { createContext, createMemo, onCleanup, useContext } from 'solid-js';
import { createStore, produce } from 'solid-js/store';
import { type AccountStream, type AccountStreamStatus, useAccountStream } from './accountStream';

/** The app-shell connection, plus the registry deciding what it subscribes. */
export type AccountStreamHub = {
  readonly stream: AccountStream;
  /** The token this hub dialled with. A consumer holding another opens its own. */
  readonly token: string | null;
  /** Refcounted registration; the returned function releases it. */
  readonly subscribe: (topics: readonly string[]) => () => void;
};

const HubContext = createContext<AccountStreamHub>();

/**
 * Build the shell's hub. ONE per app (`<AccountStreamProvider/>`); the topic set
 * it dials is the union of what its consumers have registered, so mounting a
 * route that reads a new topic re-dials once rather than opening a second feed.
 */
export function createAccountStreamHub(
  token: string | null,
  opts: { reconnectMs?: number; random?: () => number } = {},
): AccountStreamHub {
  const [counts, setCounts] = createStore<Record<string, number>>({});
  // The joined string is the memo's identity, so a registration churn that
  // leaves the same set — the ordinary case for a navigation that swaps one
  // route for another reading the same topics — recomputes to an equal value and
  // does NOT tear the socket down.
  const query = createMemo(() =>
    Object.entries(counts)
      .filter(([, n]) => n > 0)
      .map(([topic]) => topic)
      .sort()
      .join(','),
  );
  const stream = useAccountStream(() => (query() === '' ? [] : query().split(',')), token, opts);
  return {
    stream,
    token,
    subscribe(topics) {
      setCounts(
        produce((c) => {
          for (const t of topics) c[t] = (c[t] ?? 0) + 1;
        }),
      );
      return () =>
        setCounts(
          produce((c) => {
            for (const t of topics) c[t] = Math.max(0, (c[t] ?? 0) - 1);
          }),
        );
    },
  };
}

export const AccountStreamProviderContext = HubContext.Provider;

/**
 * The shell's hub when it dials with `token`, else `undefined`. For a read that
 * only needs the CHANGE signal (`lib/live/live-query.ts`): unlike
 * {@link useStreamTopics} it never opens a connection of its own — a socket per
 * query would be the un-multiplexed shape this file exists to end, and with no
 * shared stream such a read simply polls.
 */
export function useSharedHub(token: string | null): AccountStreamHub | undefined {
  const hub = useContext(HubContext);
  return hub !== undefined && token !== null && hub.token === token ? hub : undefined;
}

/**
 * What one consumer sees: the shared store, its OWN topics' status, and a
 * topic-scoped row read. Deliberately the same shape `useAccountStream` returned
 * to the four call sites before the hub existed, so adopting it is one import.
 */
export type TopicStream = {
  readonly store: AccountStream['store'];
  readonly status: () => AccountStreamStatus;
  readonly list: (topic: string) => unknown[];
};

/**
 * Subscribe `topics` over the shell's connection, or — with no hub above, which
 * is how a route test mounts one screen, and how a caller holding a DIFFERENT
 * token must behave — over one of this consumer's own.
 *
 * The token check is not defensive tidiness: the hub dials with the shell's
 * credential, and a route handed `props.token` for a test would otherwise be
 * silently served another principal's rows.
 */
export function useStreamTopics(topics: readonly string[], token: string | null): TopicStream {
  const hub = useContext(HubContext);
  if (hub === undefined || hub.token !== token) {
    const own = useAccountStream(() => topics, token);
    return { store: own.store, status: () => statusForTopics(own, topics), list: own.list };
  }
  onCleanup(hub.subscribe(topics));
  return {
    store: hub.stream.store,
    status: () => statusForTopics(hub.stream, topics),
    list: hub.stream.list,
  };
}

/**
 * This consumer's own liveness, which is not the connection's.
 *
 * A connection serving three topics is `live` while one of them was refused for
 * want of a scope — `partitionTopics` puts it in `ready.unauthorized` and never
 * snapshots it — so a consumer reading the raw connection status showed a green
 * pill over a board that could never fill, and (worse) suspended the REST poll
 * that was its only remaining source of rows. `error` is the honest answer: it
 * keeps the backfill polling and paints the pill as a fault.
 */
export function statusForTopics(
  stream: AccountStream,
  topics: readonly string[],
): AccountStreamStatus {
  const status = stream.status();
  if (status !== 'live') return status;
  const served = stream.served();
  // Live with no `ready` yet cannot happen (`ready` is what sets `live`), but a
  // future producer that flipped the order must not read as a served topic.
  if (served === null) return 'connecting';
  return topics.every((t) => served.has(t)) ? 'live' : 'error';
}
